import { Loader2, Pause, Play } from 'lucide-react';
import React, { useState } from 'react';

import { cn } from '@/lib/utils';
import {
  useHoldAutoCloseMutation,
  useResumeAutoCloseMutation,
  useTransitionStageMutation,
} from '@/services/api/miniTortService';

import { STAGE_TRANSITIONS } from './stage-config';
import type { MiniTortClaimDetail, PipelineStatus } from './types';
import { MiniTortStage } from './types';
import { extractApiError } from './utils';

interface MiniTortActionsProps {
  claim: MiniTortClaimDetail;
  status: PipelineStatus | undefined;
  className?: string;
}

export const MiniTortActions: React.FC<MiniTortActionsProps> = ({ claim, status, className }) => {
  const [transitionStage, { isLoading: isTransitioning }] = useTransitionStageMutation();
  const [holdAutoClose, { isLoading: isHolding }] = useHoldAutoCloseMutation();
  const [resumeAutoClose, { isLoading: isResuming }] = useResumeAutoCloseMutation();

  const [inputModal, setInputModal] = useState<{
    target: MiniTortStage;
    field: 'missing_item_prompt' | 'rejection_reason';
    label: string;
  } | null>(null);
  const [confirmModal, setConfirmModal] = useState<{
    target: MiniTortStage;
    label: string;
  } | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  const transitions = STAGE_TRANSITIONS[claim.stage] ?? [];

  const handleTransition = async (target: MiniTortStage, extra?: Record<string, string>) => {
    try {
      setError(null);
      await transitionStage({
        id: claim.id,
        body: { new_stage: target, ...extra },
      }).unwrap();
      setInputModal(null);
      setConfirmModal(null);
      setInputValue('');
    } catch (err: unknown) {
      setError(extractApiError(err, 'Transition failed'));
    }
  };

  const onActionClick = (action: (typeof transitions)[number]) => {
    if (action.requiresInput) {
      setInputModal({
        target: action.target,
        field: action.requiresInput,
        label: action.label,
      });
    } else {
      // Non-input transitions require confirmation
      setConfirmModal({ target: action.target, label: action.label });
    }
  };

  const onSubmitInput = () => {
    if (!inputModal) return;
    if (!inputValue.trim()) {
      setError('This field is required');
      return;
    }
    handleTransition(inputModal.target, { [inputModal.field]: inputValue });
  };

  const hasBlockers = status?.has_unresolved_blockers;

  return (
    <div className={cn('rounded-lg border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-900', className)}>
      <h3 className="mb-3 text-sm font-semibold text-zinc-900 dark:text-white">Actions</h3>

      {hasBlockers && (
        <div className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs font-medium text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
          Unresolved blocker flags prevent stage advancement. Resolve all blockers before transitioning.
        </div>
      )}

      {transitions.length === 0 ? (
        <p className="text-sm text-zinc-500 dark:text-zinc-400">
          No manual actions available for this stage
        </p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {transitions.map((action) => (
            <button
              type="button"
              key={action.target}
              onClick={() => onActionClick(action)}
              disabled={isTransitioning || (hasBlockers && action.target !== MiniTortStage.REJECTED)}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium transition-colors disabled:opacity-50',
                action.variant === 'destructive'
                  ? 'border border-red-200 text-red-600 hover:bg-red-50 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-900/20'
                  : 'bg-indigo-600 text-white hover:bg-indigo-700',
              )}
            >
              {isTransitioning && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {action.label}
            </button>
          ))}
        </div>
      )}

      {/* Auto-close controls */}
      {claim.stage === MiniTortStage.PAYMENT_EN_ROUTE && status && (
        <div className="mt-3 border-t border-zinc-100 pt-3 dark:border-zinc-800">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-zinc-700 dark:text-zinc-300">Auto-Close Timer</p>
              {status.auto_close_deadline_at && (
                <p className="text-xs text-zinc-500 dark:text-zinc-400">
                  Deadline:{' '}
                  {new Date(status.auto_close_deadline_at).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </p>
              )}
            </div>
            {status.auto_close_on_hold ? (
              <button
                type="button"
                onClick={() => resumeAutoClose(claim.id)}
                disabled={isResuming}
                className="flex items-center gap-1.5 rounded-md border border-zinc-200 px-3 py-1.5 text-xs font-medium text-zinc-600 hover:bg-zinc-50 disabled:opacity-50 dark:border-zinc-700 dark:text-zinc-400 dark:hover:bg-zinc-800"
              >
                {isResuming ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
                Resume
              </button>
            ) : (
              <button
                type="button"
                onClick={() => holdAutoClose(claim.id)}
                disabled={isHolding}
                className="flex items-center gap-1.5 rounded-md border border-zinc-200 px-3 py-1.5 text-xs font-medium text-zinc-600 hover:bg-zinc-50 disabled:opacity-50 dark:border-zinc-700 dark:text-zinc-400 dark:hover:bg-zinc-800"
              >
                {isHolding ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Pause className="h-3.5 w-3.5" />}
                Hold
              </button>
            )}
          </div>
          {status.auto_close_on_hold && (
            <p className="mt-1 text-xs font-medium text-amber-600 dark:text-amber-400">
              Auto-close is paused
            </p>
          )}
        </div>
      )}

      {/* Chase progress */}
      {claim.chase_progress && (
        <div className="mt-3 border-t border-zinc-100 pt-3 dark:border-zinc-800">
          <p className="text-xs font-medium text-zinc-700 dark:text-zinc-300">Chase Progress</p>
          <div className="mt-1 flex items-center gap-3">
            <div className="flex-1">
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-zinc-100 dark:bg-zinc-800">
                <div
                  className="h-full rounded-full bg-indigo-500"
                  style={{
                    width: `${(claim.chase_progress.current_step / claim.chase_progress.total_steps) * 100}%`,
                  }}
                />
              </div>
            </div>
            <span className="text-xs font-medium text-zinc-500 dark:text-zinc-400">
              Step {claim.chase_progress.current_step}/{claim.chase_progress.total_steps}
            </span>
          </div>
          {claim.chase_progress.next_step_at && (
            <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
              Next follow-up:{' '}
              {new Date(claim.chase_progress.next_step_at).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
              })}
            </p>
          )}
        </div>
      )}

      {/* Confirm modal for non-input transitions */}
      {confirmModal && (
        <div className="mt-3 rounded-md border border-indigo-200 bg-indigo-50 p-3 dark:border-indigo-700 dark:bg-indigo-900/20">
          <p className="mb-2 text-sm font-medium text-zinc-800 dark:text-zinc-200">
            Transition to <strong>{confirmModal.label}</strong>?
          </p>
          <p className="mb-3 text-xs text-zinc-500 dark:text-zinc-400">This action cannot be undone.</p>
          {error && <p className="mb-2 text-xs text-red-600">{error}</p>}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => handleTransition(confirmModal.target)}
              disabled={isTransitioning}
              className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {isTransitioning && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Confirm
            </button>
            <button
              type="button"
              onClick={() => { setConfirmModal(null); setError(null); }}
              className="rounded-md px-3 py-1.5 text-sm font-medium text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-200"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Input modal */}
      {inputModal && (
        <div className="mt-3 rounded-md border border-zinc-200 bg-zinc-50 p-3 dark:border-zinc-700 dark:bg-zinc-800">
          <p className="mb-2 text-sm font-medium text-zinc-700 dark:text-zinc-300">
            {inputModal.field === 'missing_item_prompt'
              ? 'What materials are needed from the claimant?'
              : 'Why is this claim being rejected?'}
          </p>
          <textarea
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              setError(null);
            }}
            rows={3}
            className="w-full rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-700 placeholder:text-zinc-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300"
            placeholder={
              inputModal.field === 'missing_item_prompt'
                ? 'e.g., Please provide your declarations page showing your insurance coverage...'
                : 'e.g., Claim does not meet eligibility requirements because...'
            }
          />
          {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
          <div className="mt-2 flex items-center gap-2">
            <button
              type="button"
              onClick={onSubmitInput}
              disabled={isTransitioning}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium text-white',
                inputModal.field === 'rejection_reason'
                  ? 'bg-red-600 hover:bg-red-700'
                  : 'bg-indigo-600 hover:bg-indigo-700',
              )}
            >
              {isTransitioning && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {inputModal.label}
            </button>
            <button
              type="button"
              onClick={() => {
                setInputModal(null);
                setInputValue('');
                setError(null);
              }}
              className="rounded-md px-3 py-1.5 text-sm font-medium text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-200"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
